import type { Translations } from "./types"

export const uk: Translations = {
	appName: "zPlanem",

	hero: {
		sentences: [
			"Від плану до виконання",
			"Без стресу",
			"Завжди в курсі справ",
			"Підтримка ШІ на кожному кроці",
		],
	},

	language: {
		label: "Мова",
		pl: "Polski",
		en: "English",
		uk: "Українська",
	},

	common: {
		loading: "Завантаження...",
	},

	auth: {
		login: "Увійти",
		logout: "Вийти",
		googleLogin: "Продовжити з Google",
		getStarted: "Почати",
		backToMain: "Повернутися на головну",
	},

	dashboard: {
		title: "Панель керування",
		welcome: "З поверненням!",
		dummyText: "Тут з'являться ваша нерухомість та прогрес робіт.",
	},

	properties: {
		newPropertyCta: "+ Нова нерухомість",
		addFirstPropertyCta: "Додайте свою першу нерухомість",
		emptyStateTitle: "Поки немає нерухомості",
		emptyStateBody:
			"Додайте свою першу нерухомість, щоб почати планування ремонту.",
		lastOpenedLabel: "Востаннє відкрито",
		propertiesListHeading: "Ваша нерухомість",
		types: {
			apartment: "Квартира",
			house: "Будинок",
			office: "Офіс",
			other: "Інше",
		},
		tabs: {
			navLabel: "Розділи нерухомості",
			overview: "Огляд",
			projects: "Проєкти",
			tickets: "Завдання",
			journal: "Журнал",
			documents: "Документи",
			budget: "Бюджет",
		},
		form: {
			newPageTitle: "Нова нерухомість",
			nameLabel: "Назва",
			namePlaceholder: "напр. Мокотовська 12",
			typeLabel: "Тип",
			customTypeLabel: "Опишіть тип",
			customTypePlaceholder: "напр. гараж, дача",
			descriptionLabel: "Опис",
			descriptionPlaceholder: "Додаткові нотатки про цю нерухомість",
			submit: "Додати нерухомість",
			cancel: "Скасувати",
			errors: {
				nameRequired: "Назва обов'язкова.",
				nameTooLong: "Назва не може перевищувати {max} символів.",
				typeRequired: "Виберіть тип.",
				customTypeRequired: "Опишіть тип.",
				customTypeTooLong: "Власний тип не може перевищувати {max} символів.",
				descriptionTooLong: "Опис не може перевищувати {max} символів.",
				unknown: "Щось пішло не так. Спробуйте ще раз.",
			},
		},
		detail: {
			breadcrumbsNavLabel: "Навігаційний ланцюжок",
			manageLabel: "Керування",
			manageMenu: {
				edit: "Редагувати нерухомість",
				transfer: "Передати право власності",
			},
		},
		edit: {
			title: 'Редагувати "{propertyName}"',
			nameLabel: "Назва",
			descriptionLabel: "Опис",
			save: "Зберегти зміни",
			saving: "Збереження...",
			cancel: "Скасувати",
			readOnlyNotice:
				"Редагувати цю нерухомість може лише власник або члени з роллю архітектора.",
			improveWithAi: "Покращити з AI",
			addDescription: "Додати опис…",
			editName: "Редагувати назву",
			editDescription: "Редагувати опис",
			descriptionPlaceholder: "Опишіть цю нерухомість у Markdown…",
			deleteSectionTitle: "Видалити нерухомість",
			deleteSectionBody:
				"Ця дія видаляє нерухомість із вашого облікового запису. Її не можна скасувати.",
			deleteAction: "Видалити нерухомість",
			deleteConfirmTitle: "Видалити нерухомість?",
			deleteConfirmBody:
				'"{propertyName}" зникне з вашого облікового запису. Цю дію не можна скасувати.',
			deleteConfirmAction: "Видалити назавжди",
			deleteError: "Не вдалося видалити нерухомість.",
		},
		transfer: {
			title: 'Передати право власності на "{propertyName}"',
			explainer:
				"Виберіть члена цієї нерухомості, який стане новим власником. Ви залишитеся з доступом як член зі своєю поточною роллю. Скасувати може лише новий власник.",
			memberLabel: "Член",
			emptyState:
				"Немає членів для передачі. Спочатку запросіть когось зі сторінки нерухомості.",
			confirmationLabel: "Введіть назву нерухомості для підтвердження",
			confirmationPlaceholder: "{propertyName}",
			submit: "Передати право власності до {name}",
			submitting: "Передача...",
			cancel: "Скасувати",
		},
		share: {
			buttonLabel: "Поділитися",
			or: "Або",
			modalTitle: 'Поділитися "{propertyName}"',
			invite: {
				emailLabel: "Email",
				emailPlaceholder: "name@example.com",
				roleLabel: "Роль",
				submit: "Надіслати запрошення",
				submitting: "Надсилання...",
				confirm: {
					title: "Підтвердіть запрошення",
					body: "Запросити {email} як {role}?",
					send: "Надіслати запрошення",
					cancel: "Скасувати",
				},
				errors: {
					emailInvalid: "Введіть дійсну адресу електронної пошти.",
					selfInvite: "Ви не можете запросити самого себе — ви власник.",
					alreadyMember: "{email} вже є учасником.",
					unknown: "Не вдалося надіслати запрошення. Спробуйте ще раз.",
				},
			},
			members: {
				heading: "Учасники",
				ownerLabel: "Власник",
				remove: {
					button: "Видалити учасника",
					submitting: "Видалення...",
					confirm: {
						title: "Видалити учасника",
						body: "Видалити {name} з цієї нерухомості?",
						remove: "Видалити",
						cancel: "Скасувати",
					},
					errors: {
						unknown: "Не вдалося видалити учасника.",
					},
				},
				role: {
					button: "Змінити роль учасника",
					errors: {
						unknown: "Не вдалося змінити роль учасника.",
					},
				},
			},
			invitations: {
				heading: "Очікувані запрошення",
				emptyState: "Немає очікуваних запрошень.",
				expiresIn: "Закінчується через {days} дн.",
				expiresToday: "Закінчується сьогодні",
				menu: {
					actionsLabel: "Дії запрошення",
				},
				remove: {
					button: "Видалити запрошення",
					submitting: "Видалення...",
					confirm: {
						title: "Видалити запрошення",
						body: "Скасувати запрошення для {name}?",
						remove: "Видалити запрошення",
						cancel: "Скасувати",
					},
					errors: {
						unknown: "Не вдалося видалити запрошення.",
					},
				},
				resend: {
					button: "Надіслати знову",
					submitting: "Надсилання…",
					confirm: {
						title: "Надіслати запрошення знову",
						body: "Надіслати запрошення знову до {email}?",
						send: "Надіслати знову",
						cancel: "Скасувати",
					},
					errors: {
						unknown: "Не вдалося надіслати запрошення знову.",
					},
				},
				role: {
					button: "Змінити роль запрошення",
					errors: {
						unknown: "Не вдалося змінити роль запрошення.",
					},
				},
			},
			roles: {
				member: "Учасник",
				contractor: "Підрядник",
				architect: "Архітектор",
			},
			roleDescriptions: {
				member: "Загальний учасник — у курсі справ нерухомості.",
				contractor:
					"Підрядник, що виконує ремонтні чи будівельні роботи на цій нерухомості.",
				architect:
					"Проєктувальник, відповідальний за рішення з планування цієї нерухомості.",
			},
			qr: {
				generateButton: "Створити QR",
				dialogTitle: "QR запрошення — {role}",
				pinLabel: "PIN",
				instructions:
					"Нехай запрошений відсканує цей код у своєму додатку zPlanem і введе PIN.",
				instructionsNoPin:
					"Нехай запрошений відсканує цей код у своєму додатку zPlanem — його запит ви схвалите тут.",
				expiresInMmSs: "Закінчується через {mm}:{ss}",
				expired: "Прострочено",
				done: "Готово",
				generating: "Створення...",
				error: "Не вдалося створити QR-запрошення. Спробуйте ще раз.",
			},
		},
	},

	projects: {
		list: {
			heading: "Проєкти",
			createCta: "Новий проєкт",
			emptyStateTitle: "Ще немає проєктів",
			emptyStateBody: "Створіть перший проєкт для цієї нерухомості.",
			archivedHeading: "Архівовані",
			archivedBadge: "Архівований",
		},
		statuses: {
			draft: "Чернетка",
			ready_for_work: "Готовий до роботи",
			in_progress: "В процесі",
			awaiting_acceptance: "Очікує на прийняття",
			accepted: "Прийнятий",
		},
		types: {
			garden: "Сад",
			construction: "Будівництво",
			interior: "Інтер'єр",
		},
		tabs: {
			navLabel: "Розділи проєкту",
			details: "Деталі",
			tasks: "Завдання",
			visualizations: "Візуалізації та плани",
			visualizationsShort: "Візуалізації",
			inspirations: "Натхнення",
			documents: "Документи",
			budget: "Бюджет",
			members: "Учасники",
		},
		form: {
			createTitle: "Новий проєкт",
			nameLabel: "Назва",
			namePlaceholder: "напр. Оновлення саду",
			typeLabel: "Тип",
			descriptionLabel: "Опис",
			descriptionPlaceholder: "Необов'язкові нотатки про проєкт",
			submit: "Створити проєкт",
			cancel: "Скасувати",
			errors: {
				nameRequired: "Назва обов'язкова",
				nameTooLong: "Назва може мати щонайбільше {max} символів",
				typeRequired: "Оберіть тип проєкту",
				descriptionTooLong: "Опис може мати щонайбільше {max} символів",
				costInvalid: "Введіть коректну суму (напр. 12500.00)",
				unknown: "Щось пішло не так. Спробуйте ще раз.",
			},
		},
		detail: {
			statusLabel: "Статус",
			acceptedOwnerOnlyHint:
				"Лише власник нерухомості може позначити проєкт як прийнятий",
			membersHeading: "Учасники",
			addMemberCta: "Додати учасника",
			addMemberTitle: "Додати учасника проєкту",
			addMemberEmpty: "Усі, хто має право, вже в цьому проєкті",
			removeMemberLabel: "Видалити",
			archiveCta: "Архівувати",
			unarchiveCta: "Відновити",
			archivedNotice: "Цей проєкт архівовано",
			editCta: "Редагувати",
			editTitle: "Редагувати проєкт",
			save: "Зберегти",
			saving: "Збереження…",
			cancel: "Скасувати",
			estimatedExecutionCostLabel: "Орієнтовна вартість виконання",
			costPlaceholder: "напр. 12500.00",
			currencyLabel: "Валюта",
			noDescription: "Немає опису",
			descriptionHeading: "Опис",
			journalHeading: "Журнал",
		},
		cost: {
			heading: "Вартість створення проєкту",
			noCost: "Вартість ще не запропоновано",
			acceptedLabel: "Прийнята вартість",
			pendingLabel: "Очікувана пропозиція",
			proposeCta: "Запропонувати вартість",
			proposeTitle: "Запропонувати вартість",
			amountLabel: "Сума",
			amountPlaceholder: "напр. 12500.00",
			currencyLabel: "Валюта",
			submit: "Запропонувати",
			cancel: "Скасувати",
			acceptCta: "Прийняти",
			rejectCta: "Відхилити",
			markPaidCta: "Позначити як оплачений",
			markPaidHint: "Доступно після прийняття вартості",
			paidBadge: "Оплачено",
			errors: {
				amountInvalid: "Введіть коректну суму (напр. 12500.00)",
				unknown: "Щось пішло не так. Спробуйте ще раз.",
			},
		},
		visualizations: {
			emptyTitle: "Поки що немає візуалізацій",
			emptyBody: "Тут з'являться рендери, креслення та плани цього проєкту.",
			addCta: "Додати візуалізацію",
			processing: "Обробка…",
			modalTitle: "Додати візуалізацію",
			tabFile: "Завантажити файл",
			tabLink: "Додати посилання",
			chooseFile: "Вибрати файл",
			dropHere: "Перетягніть файли сюди або натисніть, щоб вибрати",
			fileHint: "Зображення, PDF або відео — до 25 МБ на файл.",
			urlLabel: "Посилання",
			urlPlaceholder: "https://…",
			titleLabel: "Назва (необов'язково)",
			descriptionLabel: "Опис (необов'язково)",
			add: "Додати",
			adding: "Додавання…",
			cancel: "Скасувати",
			download: "Завантажити",
			delete: "Видалити",
			deleteTitle: "Видалити візуалізацію?",
			deleteBody: "Це видалить її назавжди й не можна буде скасувати.",
			close: "Закрити",
			errorUnsupported: "Цей тип файлу не підтримується.",
			errorTooLarge: "Цей файл перевищує ліміт 25 МБ.",
			errorUpload: "Не вдалося додати візуалізацію. Спробуйте ще раз.",
			errorLoad: "Не вдалося завантажити візуалізації.",
			uploadedByLabel: "Завантажено: {name}",
		},
		inspirations: {
			emptyTitle: "Поки що немає натхнення",
			emptyBody: "Збирайте тут референси та ідеї для цього проєкту.",
			addCta: "Додати натхнення",
			processing: "Обробка…",
			modalTitle: "Додати натхнення",
			tabFile: "Завантажити файл",
			tabLink: "Додати посилання",
			chooseFile: "Вибрати файл",
			dropHere: "Перетягніть файли сюди або натисніть, щоб вибрати",
			fileHint: "Зображення, PDF або відео — до 5 МБ на файл.",
			urlLabel: "Посилання",
			urlPlaceholder: "https://…",
			titleLabel: "Назва (необов’язково)",
			descriptionLabel: "Опис (необов’язково)",
			add: "Додати",
			adding: "Додавання…",
			cancel: "Скасувати",
			download: "Завантажити",
			delete: "Видалити",
			deleteTitle: "Видалити натхнення?",
			deleteBody: "Це назавжди видалить його — скасувати неможливо.",
			close: "Закрити",
			errorUnsupported: "Цей тип файлу не підтримується.",
			errorTooLarge: "Файл перевищує ліміт 5 МБ.",
			errorUpload: "Не вдалося додати натхнення. Спробуйте ще раз.",
			errorLoad: "Не вдалося завантажити натхнення.",
			uploadedByLabel: "Завантажено: {name}",
		},
		mediaUpload: {
			filesSelected: {
				one: "Вибрано {count} файл",
				few: "Вибрано {count} файли",
				many: "Вибрано {count} файлів",
				other: "Вибрано {count} файла",
			},
			addFiles: {
				one: "Додати {count} файл",
				few: "Додати {count} файли",
				many: "Додати {count} файлів",
				other: "Додати {count} файла",
			},
			addingFiles: {
				one: "Додавання {count} файлу…",
				few: "Додавання {count} файлів…",
				many: "Додавання {count} файлів…",
				other: "Додавання {count} файла…",
			},
			filesSkipped: {
				one: "Пропущено {count} файл",
				few: "Пропущено {count} файли",
				many: "Пропущено {count} файлів",
				other: "Пропущено {count} файла",
			},
			filesFailed: {
				one: "Не вдалося додати {count} файл",
				few: "Не вдалося додати {count} файли",
				many: "Не вдалося додати {count} файлів",
				other: "Не вдалося додати {count} файла",
			},
			addAnother: "Додати ще файл",
			removeFile: "Видалити {name}",
			retryFailed: "Спробувати ще раз",
			statusUploading: "Завантаження…",
			statusDone: "Додано",
			statusFailed: "Не вдалося",
			errorDuplicate: "Уже у списку.",
			errorBatchLimit: "Можна додати щонайбільше {max} файлів за раз.",
			errorQuotaFull: "Цей проєкт досяг ліміту {max} елементів.",
		},
		budget: {
			title: "Бюджет",
			spentLabel: "Підтверджені витрати",
			excludedNote: "{count} без курсу — сума часткова",
			draftsLabel: "до перегляду",
			emptyTitle: "Ще немає витрат",
			emptyBody:
				"Записуйте фактичні витрати — додайте витрату вручну або надішліть рахунок, і Mareczek його зчитає.",
			addCta: "Додати витрату",
			captureCta: "Надіслати рахунок",
			formTitleAdd: "Додати витрату",
			formTitleEdit: "Редагувати витрату",
			amountLabel: "Сума",
			currencyLabel: "Валюта",
			kindLabel: "Категорія",
			kindNone: "Без категорії",
			vendorLabel: "Продавець",
			descriptionLabel: "Опис",
			occurredAtLabel: "Дата",
			projectLabel: "Проєкт",
			projectNone: "Вся нерухомість",
			save: "Зберегти",
			cancel: "Скасувати",
			amountInvalid: "Введіть коректну суму (напр. 1250.50)",
			kinds: {
				material: "Матеріали",
				labor: "Робота",
				service: "Послуги",
				equipment: "Обладнання",
				mix: "Змішані",
				other: "Інше",
			},
			statuses: {
				ai_draft: "Чернетка",
				confirmed: "Підтверджено",
				rejected: "Відхилено",
			},
			accept: "Підтвердити",
			reject: "Відхилити",
			edit: "Редагувати",
			delete: "Видалити",
			deleteConfirmBody: "Видалити цю витрату?",
			captureModalTitle: "Надіслати рахунок",
			captureHint:
				"Рахунок або чек — PDF чи фото. Mareczek зчитає суму, продавця й дату — ви підтверджуєте перед зарахуванням.",
			fileLabel: "Файл рахунку (PDF або фото)",
			capture: "Надіслати",
			capturing: "Надсилання…",
			duplicateWarning: "Схоже, цей рахунок уже надіслано.",
			extractionPending: "Зчитування рахунку…",
			proofLabel: "Рахунок",
			errorLoad: "Не вдалося завантажити бюджет.",
			errorSave: "Не вдалося зберегти витрату.",
			errorFileTooLarge: "Цей файл завеликий.",
		},
		documents: {
			emptyTitle: "Немає документів",
			emptyBody: "Завантажте PDF-и, таблиці або інші файли для цього проєкту.",
			emptyBodyProperty:
				"Завантажте PDF-и, таблиці або інші файли для цього об'єкту.",
			addCta: "Додати документ",
			uploadModalTitle: "Завантажити документ",
			titleLabel: "Назва",
			titlePlaceholder: "напр. Договір",
			fileLabel: "Файл",
			aiReadableLabel: "Дозволити Mareczek (ШІ) читати цей файл",
			upload: "Завантажити",
			uploading: "Завантаження…",
			cancel: "Скасувати",
			delete: "Видалити",
			deleteTitle: "Видалити цей документ?",
			deleteBody:
				"Це назавжди видалить \u00ab{title}\u00bb \u2014 скасувати цю дію неможливо.",
			deleteConfirm: "Видалити документ",
			download: "Завантажити",
			open: "Відкрити",
			scopeProject: "Проєкт",
			scopeProperty: "Об'єкт",
			allInProperty: "Усі документи об'єкта",
			scopeFieldLabel: "Призначити до",
			documentTypeLabel: "Тип документа",
			documentTypeOptions: {
				contract: "Договір",
				agreement: "Угода",
				notice: "Повідомлення",
				bill: "Рахунок",
				land_report: "Звіт про ділянку",
				other: "Інший",
			},
			fileDropHint: "Перетягніть файл сюди або натисніть, щоб вибрати",
			grantsModalTitle: "Поділитися документом",
			grantsCta: "Поділитися",
			ownerLabel: "Власник",
			grantToggleGrant: "Надати доступ",
			grantToggleRevoke: "Відкликати доступ",
			errorLoad: "Не вдалося завантажити документи.",
			errorFileTooLarge: "Файл перевищує ліміт 50 МБ.",
			errorUpload: "Завантаження не вдалося.",
			editCta: "Редагувати документ",
			editModalTitle: "Редагувати документ",
			save: "Зберегти",
			errorDownload: "Не вдалося відкрити файл. Спробуйте ще раз.",
			errorDownloadBlocked:
				"Дозвольте спливаючі вікна, щоб відкривати документи.",
			aiReadableHint:
				"Mareczek аналізує документи, позначені як Договір (ризики, відсутні гарантії, потенційно недобросовісні умови) або Звіт про ділянку (обмеження та ризики ділянки); інші типи документів не аналізуються.",
			aiUploadNoticeUnsupported:
				"AI зчитує лише файли PDF та зображення — файл буде завантажено, але Mareczek його не опрацює.",
			aiUploadNoticeTooLarge:
				"Файл перевищує ліміт {size} для аналізу ШІ — його буде завантажено, але Mareczek його не проаналізує.",
			aiEditNoticeTooLarge:
				"Файл перевищує ліміт {size} для аналізу ШІ, тож Mareczek його не проаналізує.",
			aiBadgeReviewing: "Аналізую",
			aiBadgeReviewingHint: "Mareczek аналізує цей документ.",
			aiBadgeReview: "Аналіз ШІ",
			aiBadgeReviewHint: "Переглянути аналіз від Mareczek.",
			aiBadgeFailed: "Аналіз не вдався",
			aiBadgeFailedHint:
				"Mareczek не зміг завершити аналіз. Відкрийте, щоб переглянути деталі.",
			aiBadgeUnsupported: "Непідтримуваний файл",
			aiBadgeUnsupportedHint: "AI зчитує лише файли PDF та зображення.",
			aiBadgeTooLarge: "Завеликий",
			aiBadgeTooLargeHint: "Файл завеликий для аналізу ШІ (ліміт {size}).",
			aiReviewModalTitle: "Аналіз документа від Mareczek",
			aiReviewProcessing: "Mareczek аналізує цей документ",
			aiReviewFailed:
				"Mareczek не зміг завершити аналіз. Вимкніть і знову увімкніть аналіз ШІ або завантажте файл ще раз, щоб спробувати знову.",
			aiReviewFailedNotContract:
				"Цей документ не схожий на договір, тож немає чого аналізувати.",
			aiReviewFailedNotLandReport:
				"Цей документ не схожий на звіт про ділянку, тож немає чого аналізувати.",
			aiReviewFailedUnreadable:
				"Mareczek не зміг прочитати цей документ. Можливо, він відсканований, зашифрований або пошкоджений.",
			aiReviewSummary: "Підсумок",
			aiReviewCritical: "Критичні проблеми",
			aiReviewAdvantages: "На вашу користь",
			aiReviewDisadvantages: "Проти вас",
			aiReviewMissing: "Відсутні гарантії",
			aiReviewPossible: "Варто уточнити",
			aiReviewAbusive: "Потенційно недобросовісні умови",
			aiReviewNoFindings: "Тут нічого не виявлено.",
			aiReviewDisclaimer:
				"Mareczek — це асистент ШІ, а не юрист. Перевіряйте все важливе, перш ніж діяти.",
			close: "Закрити",
		},
	},

	inviteEmail: {
		subject: "Вас запрошено до {propertyName} на zPlanem",
		greeting: "Вітаємо,",
		body: "{inviterName} запрошує вас до співпраці над {propertyName}. Натисніть посилання нижче, щоб прийняти запрошення.",
		cta: "Прийняти запрошення",
		signature: "— Команда zPlanem",
		expiryNote: "Це запрошення закінчується через {days} дн.",
	},

	mentionEmail: {
		subject: "{byDisplayName} згадав(-ла) вас у {propertyName}",
		greeting: "Привіт,",
		body: "{byDisplayName} згадав(-ла) вас у чаті нерухомості {propertyName}, і ви ще не бачили цього повідомлення. Відкрийте чат, щоб надолужити.",
		cta: "Відкрити чат",
		signature: "— Команда zPlanem",
	},

	invitationAcceptedEmail: {
		subject: "{accepterName} приєднався(-лася) до {propertyName}",
		greeting: "Вітаємо,",
		body: "{accepterName} прийняв(-ла) ваше запрошення до {propertyName} як {role}.",
		signature: "— Команда zPlanem",
	},

	activityDigestEmail: {
		subject: "zPlanem — підсумок активності {date}",
		intro: "Ось що змінилося у ваших нерухомостях від учора, 8:00:",
		openJournal: "Відкрити журнал",
	},

	documentSharedEmail: {
		granteeSubject: "{actor} поділився(-лася) з вами документом: {title}",
		granteeBody:
			"{actor} поділився(-лася) з вами документом \u201e{title}\u201d.",
		ownerSubject: "Вашим документом поділилися: {title}",
		ownerBody:
			"{actor} поділився(-лася) вашим документом \u201e{title}\u201d з {grantee}.",
		cta: "Відкрити документи",
	},

	acceptInvitation: {
		title: "{inviterName} запрошує вас до {propertyName} у ролі {role}",
		loadingState: "Завантаження запрошення...",
		terminalErrors: {
			notFound: "Це посилання на запрошення більше не дійсне.",
			expired:
				"Термін дії цього запрошення сплив. Попросіть власника нерухомості надіслати нове.",
			alreadyAccepted: "Це запрошення вже прийнято.",
		},
		emailMismatch:
			"Це запрошення надіслано на адресу {invitationEmail}. Вийдіть з облікового запису та увійдіть із цією адресою, щоб його прийняти.",
		signInPrompt: "Увійдіть як {invitationEmail}, щоб прийняти",
		accept: "Прийняти запрошення",
		accepting: "Прийняття...",
		success: "Ви приєдналися до {propertyName}!",
		backToDashboard: "Перейти до панелі керування",
		goToApp: "Перейти до застосунку",
		unknownInviter: "Хтось",
		qrOpenInAppTitle:
			"Відкрийте застосунок zPlanem на телефоні, щоб приєднатися до {propertyName}",
		qrOpenInAppBody:
			"Це QR-запрошення доставляється особисто — завершіть його, відсканувавши QR-код у мобільному застосунку zPlanem.",
	},

	aiIndicator: {
		processing: "AI обробляє",
		processed: "AI оброблено",
		failed: "AI помилка",
	},

	notFound: {
		title: "Цієї сторінки не існує",
	},

	chat: {
		headerName: "Mareczek",
		headerSubtitle: "AI-асистент",
		inputPlaceholder: "Напишіть Mareczek…",
		emptyState: "Почніть розмову",
		sendButtonLabel: "Надіслати повідомлення",
		openButtonLabel: "Відкрити чат",
		minimizeButtonLabel: "Згорнути чат",
		expandButtonLabel: "Розгорнути чат",
		collapseButtonLabel: "Зменшити чат",
		thinkingLabel: "Mareczek працює…",
		unreadBadge: {
			cap: "99+",
			label: "{count} непрочитаних повідомлень",
		},
		shared: {
			headerName: "Чат учасників",
			propertyHeaderName: "Чат об'єкта",
			projectHeaderName: "Чат проєкту",
			composerPlaceholder: "Напишіть повідомлення…",
			mentionSuggestionsLabel: "Підказки згадок",
			emptyState: "Поки що немає повідомлень.",
			sendButtonLabel: "Надіслати",
			closeLabel: "Закрити чат учасників",
			memberCountSingular: "{count} учасник",
			memberCountPlural: "{count} учасників",
			deletedUser: "Видалений користувач",
		},
		errors: {
			sendFailed: "Не вдалося надіслати повідомлення. Спробуйте ще раз.",
			loadFailed: "Не вдалося завантажити повідомлення. Спробуйте ще раз.",
		},
		mention: {
			toastTitle: "{name} згадав(ла) вас",
		},
	},

	mareczek: {
		greetings: {
			propertyCreated:
				"Привіт, {ownerName}! Я Мареček, твій AI-помічник у zPlanem. Я допоможу тобі планувати та керувати об'єктом {propertyName} — пиши коли захочеш.",
			propertyJoined:
				"Привіт, {memberName}! Ласкаво просимо до {propertyName}. Я Мареček, твій AI-помічник у zPlanem — пиши коли захочеш.",
		},
		systemMessages: {
			memberJoined: "{fullName} приєднався(лася) до об'єкта як {role}.",
		},
		agentActions: {
			executed: "Готово — дію виконано.",
			executedSummary: "Готово — {summary}.",
			failed: "Не вдалося виконати дію.",
			declined: "Дію відхилено.",
			chainFailed:
				"Щось пішло не так з мого боку — я не зміг завершити цю відповідь.",
			card: {
				titles: {
					"create-project": "Створити проєкт",
					"update-project": "Оновити проєкт",
					"propose-cost": "Запропонувати вартість",
					"archive-project": "Архівувати проєкт",
					"invite-member": "Запросити до об'єкта",
					"resend-invitation": "Надіслати запрошення повторно",
					"create-ticket": "Створити заявку",
					"update-ticket": "Оновити заявку",
					"assign-ticket": "Призначити заявку",
					"set-ticket-status": "Змінити статус заявки",
					"set-ticket-reminder": "Установити нагадування для заявки",
					"add-ticket-comment": "Додати коментар до заявки",
					"delete-ticket": "Видалити заявку",
					"delete-ticket-comment": "Видалити коментар заявки",
					"remove-ticket-attachment": "Видалити вкладення заявки",
					"rename-property": "Перейменувати нерухомість",
					"update-property-description": "Змінити опис нерухомості",
					"create-inspiration-from-url": "Додати натхнення з посилання",
					"create-visualization-from-url": "Додати візуалізацію з посилання",
					"create-budget-entry": "Записати витрату",
					"update-budget-entry": "Змінити витрату",
					"delete-budget-entry": "Видалити витрату",
					generic: "Запропонована дія",
				},
				accept: "Затвердити",
				decline: "Відхилити",
				statusAccepted: "Затверджено",
				statusDeclined: "Відхилено",
				statusExpired: "Термін минув",
				statusAwaitingOwner: "Очікує підтвердження від замовника",
			},
		},
	},

	notificationsBell: {
		title: "Сповіщення",
		close: "Закрити",
		empty: "Нічого нового",
		markAllRead: "Позначити всі як прочитані",
		chatMention: "{actor} згадав(-ла) вас у чаті",
		ticketAssigned: "{actor} призначив(-ла) вам завдання «{title}»",
		joinRequestCreated: "{actor} просить приєднатися до вашої нерухомості",
		agentActionPending: "Mareczek підготував дію, що чекає на ваше схвалення",
	},

	mediaModal: {
		prev: "Попередній елемент",
		next: "Наступний елемент",
	},

	journal: {
		emptyTitle: "Поки немає активності",
		emptyDescription: "Події з'являтимуться тут у міру їх виникнення.",
		errorTitle: "Не вдалося завантажити журнал",
		errorRetry: "Спробувати ще раз",
		deletedUser: "Видалений користувач",
		sourceProperty: "Нерухомість",
		events: {
			propertyCreated: {
				label: "Власність створено",
				aria: "Власність створено користувачем {actor}",
			},
			memberInvitedEmail: {
				label: "Надіслано запрошення електронною поштою",
				aria: "{actor} надіслав(ла) запрошення електронною поштою",
			},
			memberInvitedQr: {
				label: "Згенеровано запрошення QR",
				aria: "{actor} згенерував(ла) запрошення QR",
			},
			memberJoined: {
				label: "Приєднався(лась) як {role}",
				aria: "{actor} приєднався(лась) як {role}",
			},
			unknown: {
				label: "Активність",
				aria: "Зареєстрована активність",
			},
			projectCreated: {
				label: "Проєкт створено",
				aria: "{actor} створив(ла) проєкт",
			},
			projectStatusChanged: {
				label: "Зміна статусу: {from} → {to}",
				aria: "{actor} змінив(ла) статус з {from} на {to}",
			},
			projectCostProposed: {
				label: "Запропоновано вартість: {amount} {currency}",
				aria: "{actor} запропонував(ла) вартість {amount} {currency}",
			},
			projectCostAccepted: {
				label: "Прийнято вартість: {amount} {currency}",
				aria: "{actor} прийняв(ла) вартість {amount} {currency}",
			},
			projectCostRejected: {
				label: "Відхилено вартість: {amount} {currency}",
				aria: "{actor} відхилив(ла) вартість {amount} {currency}",
			},
			projectPaid: {
				label: "Позначено як оплачений: {amount} {currency}",
				aria: "{actor} позначив(ла) проєкт як оплачений ({amount} {currency})",
			},
			projectMemberAdded: {
				label: "Додано до проєкту: {target}",
				aria: "{actor} додав(ла) {target} до проєкту",
			},
			projectMemberRemoved: {
				label: "Видалено з проєкту: {target}",
				aria: "{actor} видалив(ла) {target} з проєкту",
			},
			projectArchived: {
				label: "Проєкт заархівовано",
				aria: "{actor} заархівував(ла) проєкт",
			},
			projectUnarchived: {
				label: "Проєкт відновлено",
				aria: "{actor} відновив(ла) проєкт",
			},
			inspirationCreated: {
				label: "Натхнення додано",
				aria: "{actor} додав(ла) натхнення",
			},
			inspirationDeleted: {
				label: "Натхнення вилучено",
				aria: "{actor} вилучив(ла) натхнення",
			},
			visualizationCreated: {
				label: "Візуалізацію додано",
				aria: "{actor} додав(ла) візуалізацію",
			},
			visualizationDeleted: {
				label: "Візуалізацію вилучено",
				aria: "{actor} вилучив(ла) візуалізацію",
			},
			documentCreated: {
				label: "Документ додано",
				aria: "{actor} додав(ла) документ",
			},
			documentDeleted: {
				label: "Документ видалено",
				aria: "{actor} видалив(ла) документ",
			},
			documentUpdated: {
				label: "Документ оновлено",
				aria: "{actor} оновив(ла) документ",
			},
			ticketCreated: {
				label: "Додано завдання: {title}",
				aria: "{actor} додав(-ла) завдання: {title}",
			},
			ticketCreatedDefect: {
				label: "Повідомлено про дефект: {title}",
				aria: "{actor} повідомив(-ла) про дефект: {title}",
			},
			ticketStatusChanged: {
				label: "{title}: {from} → {to}",
				aria: "{actor} змінив(-ла) статус {title} з {from} на {to}",
			},
			ticketAssigned: {
				label: "{title} → {assignee}",
				aria: "{actor} призначив(-ла) {title} для {assignee}",
			},
			ticketUnassigned: {
				label: "{title}: без призначення",
				aria: "{actor} зняв(-ла) призначення {title}",
			},
		},
	},

	tickets: {
		emptyTitle: "Поки немає завдань",
		emptyDescription: "Завдання з'являтимуться тут після додавання.",
		emptyFilteredTitle: "Немає завдань для цих фільтрів",
		emptyFilteredDescription:
			"Очистьте фільтри, щоб побачити всі завдання в цій області.",
		errorTitle: "Не вдалося завантажити завдання",
		errorRetry: "Спробувати ще раз",
		createCta: "Додати завдання",
		createTitle: "Нове завдання",
		titleLabel: "Назва",
		titlePlaceholder: "напр. Замовити плитку",
		kindLabel: "Тип",
		assigneeSelectLabel: "Виконавець",
		submitCreate: "Додати",
		creating: "Додавання...",
		statusLabel: "Статус",
		assignChangeError: "Не вдалося змінити виконавця",
		statusChangeError: "Не вдалося змінити статус",
		loadMore: "Показати більше",
		loadingMore: "Завантаження...",
		boardAriaLabel: "Дошка завдань",
		viewSwitcherLabel: "Вигляд завдань",
		viewBoard: "Дошка",
		viewList: "Список",
		listAriaLabel: "Список завдань",
		unassigned: "Без виконавця",
		dueLabel: "Термін",
		createdLabel: "Створено",
		updatedLabel: "Оновлено",
		assigneeLabel: "Виконавець",
		reminderLabel: "Нагадування",
		reminderSave: "Зберегти",
		reminderClear: "Очистити",
		reminderChangeError: "Не вдалося змінити нагадування",
		descriptionTitle: "Опис",
		noDescription: "Без опису",
		backToList: "Назад до завдань",
		backToMyTasks: "Назад до моїх завдань",
		backToProjectTasks: "Назад до завдань проєкту",
		attachmentsTitle: "Вкладення",
		noAttachments: "Немає вкладень",
		attachmentUpload: "Додати файл",
		attachmentUploading: "Завантаження...",
		attachmentOpen: "Відкрити вкладення",
		attachmentDelete: "Видалити",
		attachmentUploadError: "Не вдалося додати вкладення",
		attachmentDeleteError: "Не вдалося видалити вкладення",
		commentsTitle: "Коментарі",
		commentsEmpty: "Немає коментарів",
		commentPlaceholder: "Додати коментар",
		commentSubmit: "Додати коментар",
		commentSubmitting: "Додавання...",
		commentCreateError: "Не вдалося додати коментар",
		commentEdit: "Редагувати",
		commentSave: "Зберегти",
		commentCancel: "Скасувати",
		commentEditError: "Не вдалося оновити коментар",
		commentDelete: "Видалити",
		commentDeleteError: "Не вдалося видалити коментар",
		commentEdited: "змінено",
		mentionSuggestionsLabel: "Підказки згадок",
		myTicketsTitle: "Мої завдання",
		myTicketsEmptyTitle: "Немає завдань, призначених вам або створених вами",
		myTicketsEmptyDescription:
			"Завдання, призначені вам або створені вами, з'являтимуться тут.",
		myTicketsFilteredEmptyTitle: "Немає завдань для цих фільтрів",
		myTicketsFilteredEmptyDescription:
			"Очистьте фільтри, щоб побачити всі свої завдання.",
		myTicketsAllStatuses: "Усі",
		filtersLabel: "Фільтри завдань",
		filterProperty: "Нерухомість",
		filterProject: "Проєкт",
		filterAssignee: "Виконавець",
		filterTopic: "Тема",
		filterKind: "Тип",
		filterAllProperties: "Уся нерухомість",
		filterAllProjects: "Усі проєкти",
		filterAllAssignees: "Усі виконавці",
		filterAllTopics: "Усі теми",
		filterAllKinds: "Усі типи",
		filterClear: "Очистити",
		statusCountLabel: "Статус {status}: {count}",
		topicUngrouped: "Без теми",
		topicManage: "Теми",
		topicCreate: "Додати тему",
		topicRename: "Перейменувати",
		topicDelete: "Видалити",
		topicDeleteConfirm: "Видалити цю тему?",
		topicNameLabel: "Назва теми",
		topicNamePlaceholder: "наприклад, Кухня",
		topicScopeLabel: "Область",
		topicScopeProperty: "Уся нерухомість",
		topicCreateError: "Не вдалося додати тему",
		topicUpdateError: "Не вдалося оновити тему",
		topicDeleteError: "Не вдалося видалити тему",
		status: {
			open: "Відкриті",
			in_progress: "У роботі",
			done: "Зроблено",
			cancelled: "Скасовано",
		},
		kind: {
			task: "Завдання",
			defect: "Дефект",
		},
		formErrors: {
			titleRequired: "Введіть назву завдання",
			titleTooLong: "Назва може містити щонайбільше {max} символів",
			descriptionTooLong: "Опис може містити щонайбільше {max} символів",
			unknown: "Не вдалося зберегти завдання",
		},
	},

	userMainRoles: {
		investor: "Інвестор",
		contractor: "Підрядник",
		architect: "Архітектор",
	},

	apiErrors: {
		auth: {
			unauthorized: "Не авторизовано",
			forbidden: "У вас немає дозволу на цю операцію",
			onboardingRequired: "Потрібно завершити реєстрацію",
			emailRequired: "Потрібна адреса електронної пошти",
			emailAlreadyAssociated:
				"Адреса електронної пошти вже пов'язана з іншим обліковим записом",
		},
		validation: {
			failed: "Помилка валідації",
		},
		user: {
			notSynced:
				"Користувача не знайдено. Спочатку викличте /api/common/auth/sync.",
		},
		notifications: {
			notFound: "Сповіщення не знайдено",
			cursorInvalid: "Недійсний курсор сторінки — почніть спочатку",
		},
		property: {
			notFound: "Нерухомість не знайдено",
			forbidden: "У вас немає доступу до цієї нерухомості",
			transferConfirmationMismatch:
				"Назва підтвердження не збігається. Введіть точну назву нерухомості.",
			transferSelf: "Ви не можете передати нерухомість самому собі.",
			transferNotAMember:
				"Вибраний користувач більше не є членом цієї нерухомості.",
		},
		projects: {
			notFound: "Проєкт не знайдено",
			forbidden: "У вас немає прав для цієї дії в цьому проєкті",
			constructionRequiresHouse:
				"Будівельний проєкт можна створити лише для будинку",
			statusAcceptedOwnerOnly:
				"Лише власник нерухомості може позначити проєкт як прийнятий",
			memberNotEligible: "Цього користувача не можна додати до проєкту",
			memberAlreadyAdded: "Цей користувач уже є учасником проєкту",
			memberNotFound: "Учасника проєкту не знайдено",
			proposalNotFound: "Пропозицію вартості не знайдено",
			proposalAlreadyDecided: "Цю пропозицію вартості вже розглянуто",
			proposalConflict:
				"Пропозиція вартості щойно змінилася. Оновіть сторінку та спробуйте ще раз.",
			proposalsLockedPaid:
				"Проєкт уже оплачено — пропозиції вартості заблоковано",
			paidRequiresAcceptedCost:
				"Прийміть пропозицію вартості, перш ніж позначити проєкт як оплачений",
			alreadyPaid: "Проєкт уже позначено як оплачений",
		},
		agentActions: {
			notFound: "Дію не знайдено",
			alreadyResolved: "Цю дію вже вирішено",
			expired: "Термін дії цієї дії минув",
		},
		inspirations: {
			notFound: "Натхнення не знайдено",
			forbidden: "У вас немає дозволу на цю дію",
			unsupportedContentType: "Цей тип файлу не підтримується",
			fileTooLarge: "Цей файл завеликий",
			quotaExceeded: "Цей проєкт досяг ліміту натхнень",
			uploadUrlFailed: "Не вдалося розпочати завантаження. Спробуйте ще раз",
			uploadNotPending: "Це завантаження більше не очікує підтвердження",
			uploadObjectMissing: "Завантажений файл не знайдено",
			uploadInvalid: "Завантажений файл недійсний",
			storageError: "Сховище тимчасово недоступне. Спробуйте ще раз",
			noFields: "Немає змін для збереження",
		},
		visualizations: {
			notFound: "Візуалізацію не знайдено",
			forbidden: "У вас немає дозволу на цю дію",
			unsupportedContentType: "Цей тип файлу не підтримується",
			fileTooLarge: "Цей файл завеликий",
			quotaExceeded: "Цей проєкт досяг ліміту візуалізацій",
			uploadUrlFailed: "Не вдалося розпочати завантаження. Спробуйте ще раз",
			uploadNotPending: "Це завантаження більше не очікує підтвердження",
			uploadObjectMissing: "Завантажений файл не знайдено",
			uploadInvalid: "Завантажений файл недійсний",
			storageError: "Сховище тимчасово недоступне. Спробуйте ще раз",
			noFields: "Немає змін для збереження",
		},
		invitation: {
			notFound: "Запрошення не знайдено",
			alreadyAccepted: "Запрошення вже прийнято",
			expired: "Термін дії запрошення сплив",
			selfInvite: "Ви не можете запросити самого себе — ви власник.",
			alreadyMember: "Користувач уже є учасником",
			emailMismatch:
				"Це запрошення надіслано на адресу {email}. Увійдіть із цією адресою, щоб його прийняти.",
			wrongPin: "Невірний PIN. Попросіть запрошувача показати код ще раз.",
			resendQrUnsupported: "QR-запрошення не можна надсилати повторно.",
		},
		membership: {
			alreadyMember: "Ви вже є учасником",
			notFound: "Участь не знайдено",
			cannotRemoveOwner: "Власника об'єкта не можна видалити",
			cannotChangeOwnerRole: "Роль власника не можна змінити",
		},
		realtime: {
			channelForbidden: "У вас немає доступу до цього каналу.",
			unsupportedChannel: "Непідтримуваний канал.",
		},
		chat: {
			forbidden: "У вас немає доступу до цього чату.",
			acknowledgmentInvalid: "Підтвердження прочитання недійсне.",
			participantRequired: "Ви не є учасником цього чату.",
		},
		fcm: {
			tokenNotFound: "Токен сповіщень не знайдено.",
		},
		journal: {
			cursorInvalid: "Курсор журналу недійсний або пошкоджений.",
			limitOutOfRange: "Ліміт журналу має бути в межах від 1 до 100.",
		},
		tickets: {
			notFound: "Завдання не знайдено",
			forbidden: "У вас немає дозволу на цю дію із цим завданням",
			projectScopeMismatch: "Цей проєкт не належить до цієї нерухомості",
			topicScopeMismatch: "Ця тема не належить до цієї нерухомості",
			assigneeNotMember: "Призначити можна лише учаснику нерухомості",
			invalidTransition: "Така зміна статусу заборонена",
			cursorInvalid: "Курсор завдань недійсний або пошкоджений.",
			limitOutOfRange: "Ліміт завдань має бути в межах від 1 до 100.",
			statusConflict: "Завдання щойно змінилося. Оновіть і спробуйте ще раз.",
		},
		ticketComments: {
			notFound: "Коментар не знайдено",
			forbidden: "У вас немає дозволу видалити цей коментар",
		},
		ticketAttachments: {
			notFound: "Вкладення не знайдено",
			forbidden: "У вас немає дозволу видалити це вкладення",
			unsupportedContentType: "Цей тип файлу не підтримується",
			fileTooLarge: "Цей файл занадто великий",
			validationFailed: "Завантажений файл недійсний",
		},
		mediaUploads: {
			notFound: "Завантажене зображення не знайдено",
			forbidden: "У вас немає дозволу використовувати це зображення",
			propertyRequired: "Для цього завантаження потрібна нерухомість",
			unsupportedContentType: "Цей тип зображення не підтримується",
			fileTooLarge: "Це зображення завелике",
			validationFailed: "Завантажене зображення недійсне",
			objectMissing: "Завантажене зображення не знайдено",
		},
		documents: {
			notFound: "Документ не знайдено",
			forbidden: "У вас немає дозволу на цю дію",
			unsupportedContentType: "Цей тип файлу не підтримується",
			fileTooLarge: "Цей файл завеликий",
			validationFailed: "Завантажений файл недійсний",
			granteeNotMember: "Поділитися можна лише з учасником нерухомості",
			objectMissing: "Завантажений файл не знайдено",
		},
		topics: {
			notFound: "Тему не знайдено",
			forbidden: "Лише менеджери нерухомості можуть керувати темами",
			projectScopeMismatch: "Цей проєкт не належить цій нерухомості",
		},
		budgetEntries: {
			notFound: "Запис бюджету не знайдено",
			forbidden: "Лише власник нерухомості може керувати бюджетом",
			projectScopeMismatch: "Цей проєкт не належить цій нерухомості",
			topicScopeMismatch: "Ця тема не належить цій нерухомості",
			amountRequired: "Потрібно вказати суму",
			amountInvalid: "Ця сума недійсна",
			invalidStatus: "Така зміна статусу не дозволена",
			statusConflict:
				"Запис бюджету щойно змінився. Оновіть і спробуйте ще раз.",
			noFields: "Немає змін для збереження",
			cursorInvalid: "Курсор бюджету недійсний або пошкоджений.",
			limitOutOfRange: "Ліміт бюджету має бути в межах від 1 до 100.",
			unsupportedContentType: "Цей тип файлу не підтримується",
			fileTooLarge: "Цей файл завеликий",
			uploadUrlFailed: "Не вдалося підготувати завантаження. Спробуйте ще раз.",
			objectMissing:
				"Завантажений файл відсутній. Спробуйте завантажити ще раз.",
			validationFailed: "Завантажений файл не пройшов перевірку",
			storageError: "Помилка сховища. Спробуйте ще раз.",
			documentNotReady: "Документ ще не готовий",
		},
	},

	apiPushCopy: {
		chat: {
			titlePrivate: "{property} · Маречек",
			titleShared: "{property}",
			bodyPreviewFallback: "(порожнє повідомлення)",
		},
		journal: {
			memberInvited: "{actor} запросив(ла) нового учасника ({role})",
			memberJoined: "{actor} приєднався(лась) до нерухомості",
			propertyCreated: "{actor} створив(ла) нерухомість",
			projectCreated: "{actor} створив(ла) проєкт {project}",
			projectStatusChanged:
				"{actor} змінив(ла) статус {project}: {from} → {to}",
			projectCostProposed:
				"{actor} запропонував(ла) витрату {amount} {currency} у {project}",
			projectCostAccepted:
				"{actor} прийняв(ла) витрату {amount} {currency} у {project}",
			projectCostRejected:
				"{actor} відхилив(ла) витрату {amount} {currency} у {project}",
			projectPaid:
				"{actor} позначив(ла) {amount} {currency} як сплачене у {project}",
			projectMemberAdded: "{actor} додав(ла) {member} до {project}",
			projectMemberRemoved: "{actor} вилучив(ла) {member} з {project}",
			projectArchived: "{actor} архівував(ла) {project}",
			projectUnarchived: "{actor} відновив(ла) {project}",
			inspirationCreated: "{actor} додав(ла) натхнення до {project}",
			inspirationDeleted: "{actor} вилучив(ла) натхнення з {project}",
			visualizationCreated: "{actor} додав(ла) візуалізацію до {project}",
			visualizationDeleted: "{actor} вилучив(ла) візуалізацію з {project}",
			documentCreated: "{actor} додав(ла) документ",
			documentDeleted: "{actor} вилучив(ла) документ",
			documentUpdated: "{actor} оновив(ла) документ",
		},
		property: {
			joinApproved: "Вас додано до {property}.",
			joinRequest: "{requester} просить приєднатися до нерухомості",
		},
		ticket: {
			created: "{actor} додав(ла) завдання: {title}",
			createdDefect: "{actor} повідомив(ла) про дефект: {title}",
			assigned: "{actor} призначив(ла) вам: {title}",
			statusChanged: "{title}: {status}",
			statusDone: "{actor} завершив(ла): {title}",
			statusDoneDefect: "{actor} виправив(ла) дефект: {title}",
			reminder: "Нагадування: {title}",
			reminderDefect: "Нагадування про дефект: {title}",
			commentMention: "{actor} згадав(ла) вас у коментарі: {title}",
			commentAdded: "{actor} прокоментував(ла): {title}",
			mention: "{actor} згадав(ла) вас у заявці: {title}",
		},
	},

	sidebar: {
		navLabel: "Головна навігація",
		properties: "Нерухомість",
		myTickets: "Мої завдання",
		profile: "Профіль",
		settings: "Налаштування",
		signOut: "Вийти",
		more: "Більше",
		collapseMenu: "Згорнути меню",
		expandMenu: "Розгорнути меню",
	},

	settings: {
		title: "Налаштування",
		notifications: {
			title: "Сповіщення",
			masterToggle: "Дозволити сповіщення",
			quietHoursToggle: "Тихі години",
			quietHoursStart: "З",
			quietHoursEnd: "До",
			propertiesTitle: "Вимкнені об'єкти",
			muteProperty: "Вимкнути {property}",
			save: "Зберегти сповіщення",
			savedTimeZone: "Збережений часовий пояс",
			loading: "Завантаження сповіщень",
			error: "Не вдалося зберегти налаштування сповіщень.",
		},
	},

	theme: {
		light: "світлий",
		dark: "темний",
		auto: "авто",
		toggleLabel: "Тема",
	},

	profile: {
		title: "Профіль",
		firstNameLabel: "Ім'я",
		firstNamePlaceholder: "Олена",
		lastNameLabel: "Прізвище",
		lastNamePlaceholder: "Шевченко",
		languageLabel: "Мова",
		aiContextLabel: "Контекст для ШІ",
		aiContextPlaceholder:
			"Короткий контекст про вас, який ми передаватимемо ШІ: хто ви, в яких одиницях працюєте, якою мовою очікуєте відповідей.",
		mainRoleLabel: "Основна роль",
		save: "Зберегти",
		cancel: "Скасувати",
		saved: "Збережено",
		deleteAccount: {
			sectionTitle: "Видалити акаунт",
			sectionBody:
				"Видаляє акаунт, персональні дані, доступ і об'єкти, власником яких ви є. Повідомлення чату залишаються в історії як Видалений користувач.",
			open: "Видалити акаунт",
			dialogTitle: "Видалити акаунт?",
			dialogBody:
				"Акаунт, персональні дані, ваші об'єкти, запрошення та членства буде видалено. Повідомлення чату залишаться видимими як Видалений користувач. Цю дію не можна скасувати.",
			confirmationPhrase: "ВИДАЛИТИ",
			phraseLabel: "Введіть {phrase}, щоб підтвердити",
			confirm: "Видалити акаунт",
			submitting: "Видалення...",
			cancel: "Скасувати",
			error: "Не вдалося видалити акаунт.",
		},
		errors: {
			firstNameRequired: "Ім'я обов'язкове.",
			firstNameTooLong: "Максимальна довжина: {max} символів.",
			lastNameRequired: "Прізвище обов'язкове.",
			lastNameTooLong: "Максимальна довжина: {max} символів.",
			aiContextTooLong: "Максимальна довжина: {max} символів.",
			mainRoleRequired: "Виберіть роль.",
			unknown: "Не вдалося зберегти профіль.",
		},
	},

	onboarding: {
		welcomeHeading: "Ласкаво просимо до zPlanem!",
		justOneMoreThing: "Ще одна річ",
		subtitle: "Розкажіть про себе, щоб ми налаштували роботу з вами.",
		firstNameLabel: "Ім'я",
		firstNamePlaceholder: "Олена",
		lastNameLabel: "Прізвище",
		lastNamePlaceholder: "Шевченко",
		languageLabel: "Мова",
		mainRoleLabel: "Я…",
		mainRolePlaceholder: "Виберіть свою роль",
		termsLabel: "Я приймаю {link}",
		termsLink: "Умови використання",
		submit: "Почати",
		errors: {
			firstNameRequired: "Ім'я обов'язкове.",
			firstNameTooLong: "Максимальна довжина: {max} символів.",
			lastNameRequired: "Прізвище обов'язкове.",
			lastNameTooLong: "Максимальна довжина: {max} символів.",
			mainRoleRequired: "Виберіть роль.",
			termsRequired: "Ви повинні прийняти Умови використання.",
			unknown: "Не вдалося завершити реєстрацію.",
		},
	},
}
